/**
 * useLiveAppBanner — le bandeau « Installer l'appli » du live, dans un navigateur de téléphone.
 *
 * Les règles et les mesures de l'ancien `LiveWebMobileBanner`, à l'identique :
 *  - il ne se montre que sur le web d'un téléphone ou d'une tablette (`isWebMobile`, la règle de
 *    `liveBrowserChoiceFor`), tranché une fois : l'agent du navigateur ne change pas en séance ;
 *  - `live_web_mobile_banner.shown` une fois par live (`liveMode.liveWebMobileBannerShown`, remis à
 *    zéro à la sortie du live par `RESET_LIVE_MODE`), `.collapsed` à la croix, `.expanded` à la
 *    ligne repliée, chacun avec `role: "student"` (l'écran n'est rendu qu'à l'élève) ;
 *  - le replié vit dans `liveMode.liveWebMobileBannerCollapsed`, comme aujourd'hui ;
 *  - « Installer » : `download_app.store_click` (`source: live_web_mobile_banner`), puis la fiche de
 *    l'app (la configuration serveur, celle du build, le lien de repli) dans un nouvel onglet.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import {
    setLiveWebMobileBannerCollapsed,
    setLiveWebMobileBannerShown,
} from "../../../../../../data/redux/actions/liveMode"
import { liveBrowserChoiceFor } from "../../../../adapters/student/LiveEntry/liveBrowserAdapter"
import { browserUserAgent, openLiveAppStore } from "../../../hooks/openLiveAppStore"

type RootState = {
    liveMode?: { liveWebMobileBannerCollapsed?: boolean; liveWebMobileBannerShown?: boolean }
    data?: { staticData?: { ios_app_url?: string | null; android_app_url?: string | null } }
}

/** Le web d'un téléphone ou d'une tablette, tranché une fois : l'agent ne change pas en séance. */
const SHOW_BANNER = liveBrowserChoiceFor({ isWeb: Platform.OS === "web", userAgent: browserUserAgent() }) === "phone"

const ROLE = { role: "student" }

export type LiveAppBannerModel = {
    collapsed: boolean
    onInstall: () => void
    onCollapse: () => void
    onExpand: () => void
}

export function useLiveAppBanner(show: boolean = SHOW_BANNER): LiveAppBannerModel | null {
    const dispatch = useDispatch()
    const collapsed = useSelector((state: RootState) => state.liveMode?.liveWebMobileBannerCollapsed ?? false)
    const alreadyShown = useSelector((state: RootState) => state.liveMode?.liveWebMobileBannerShown ?? false)
    const iosAppUrl = useSelector((state: RootState) => state.data?.staticData?.ios_app_url)
    const androidAppUrl = useSelector((state: RootState) => state.data?.staticData?.android_app_url)

    useEffect(() => {
        if (!show || alreadyShown) return
        dispatch(setLiveWebMobileBannerShown() as never)
        amplitude.track("live_web_mobile_banner.shown", ROLE)
    }, [show, alreadyShown, dispatch])

    const onInstall = useCallback(
        () => openLiveAppStore("live_web_mobile_banner", { iosAppUrl, androidAppUrl }),
        [iosAppUrl, androidAppUrl]
    )

    const onCollapse = useCallback(() => {
        amplitude.track("live_web_mobile_banner.collapsed", ROLE)
        dispatch(setLiveWebMobileBannerCollapsed(true) as never)
    }, [dispatch])

    const onExpand = useCallback(() => {
        amplitude.track("live_web_mobile_banner.expanded", ROLE)
        dispatch(setLiveWebMobileBannerCollapsed(false) as never)
    }, [dispatch])

    return show ? { collapsed, onInstall, onCollapse, onExpand } : null
}
